import React, { useState, useMemo } from 'react';
import {Button, message, Table} from 'antd';
import type { TableColumnsType } from 'antd';
import VehicleFormDrawer from './components/VehicleFormDrawer';
import { VehicleType, VehicleTypeDetail } from '../../../graphql/types/VehicleType';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import { useQuery } from '@apollo/client';
import { GET_VEHICLES, FIND_VEHICLE_BY_ID, VehiclesResponse, FindVehicleByIdResponse } from './graphql/queries/getVehicles';
import { UPDATE_VEHICLE } from './graphql/mutations/vehicleMutations';
import { useMutation } from '@apollo/client';
import { formatNumberWithCommas } from '../../../utils/numberUtils';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { useTranslation } from 'react-i18next';
import { useVehicleTypes } from '../../../hooks/useVehicleTypeOptions';
import TablePaginationBar from '../../../components/TablePaginationBar';


const VehiclePage: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleTypeDetail | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [tempFilters, setTempFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const { currentWarehouse: warehouse } = useWarehouseContext();
  const { t, i18n } = useTranslation();
  const statusOptions = [
    { label: t('enum.activeStatus.active'), value: 1 },
    { label: t('enum.activeStatus.suspended'), value: 0 },
  ];
  
  const { data, loading, refetch } = useQuery<VehiclesResponse>(GET_VEHICLES, {
    variables: {
      ...filters,
      warehouseId: warehouse?.id
    },
    skip: !warehouse?.id,
    fetchPolicy: 'network-only'
  });

  // 查询单个车辆详情
  const { refetch: refetchVehicleDetail } = useQuery<FindVehicleByIdResponse>(
    FIND_VEHICLE_BY_ID,
    {
      variables: { id: null },
      skip: true,
      fetchPolicy: 'network-only',
    }
  );

  const allVehicles = useMemo(() => data?.allVehicles || [], [data?.allVehicles]);
  const pagedVehicles = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    return allVehicles.slice(start, start + pagination.pageSize);
  }, [allVehicles, pagination]);

  const handleFilter = (newFilters: FilterOptions) => {
    setFilters({ ...newFilters }); // 保证是新对象
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (name: string, value: any) => {
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    handleFilter({ ...tempFilters });
  };

  const resetFilters = () => {
    setTempFilters(DEFAULT_FILTER_OPTIONS);
    handleFilter(DEFAULT_FILTER_OPTIONS);
  };

  const handleTableChange = (pagination: any) => {
    setPagination({ current: pagination.current, pageSize: pagination.pageSize });
  };

  const showNewDrawer = () => {
    setSelectedVehicle(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = async (record: VehicleType) => {
    try {
      const result = await refetchVehicleDetail({ id: Number(record.id) });
      const vehicleDetail = result.data?.findVehicleById;
      
      if (vehicleDetail) {
        setSelectedVehicle(vehicleDetail);
        setDrawerMode('edit');
        setDrawerVisible(true);
      } else {
        message.error('Vehicle detail not found for ID:', record.id);
      }
    } catch (error) {
      console.error('Failed to load vehicle details:', error);
    }
  };

  const [updateVehicle] = useMutation<Boolean>(UPDATE_VEHICLE);

  const handleDrawerSubmit = async (values: VehicleTypeDetail) => {
    if (drawerMode === 'create') {
      // 新增逻辑
    } else if (drawerMode === 'edit' && selectedVehicle) {
      await updateVehicle({ variables: { input: values } });
    }
    setDrawerVisible(false);
    // 重新查询列表数据
    refetch();
  };

  // 获取车型下拉数据
  const { vehicleTypes } = useVehicleTypes();
  const modelOptions = (vehicleTypes || []).map(vt => ({
    label: vt.nameEn,
    value: vt.id,
    status: vt.status
  }));

  const columns: TableColumnsType<VehicleType> = [
    {
      title: t('pickup.vehicle.columns.id'),
      dataIndex: 'vehicleNumber',
      key: 'vehicleNumber',
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (text: string, record: VehicleType) => (
        <Button type="link" onClick={() => showEditDrawer(record)} style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}>
            {text}
        </Button>
      ),
    },
    { 
      title: t('pickup.vehicle.columns.licensePlate'), 
      dataIndex: 'licensePlate', 
      key: 'licensePlate', 
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    {
      title: t('pickup.vehicle.columns.model'),
      dataIndex: 'vehicleTypeId',
      key: 'vehicleTypeId',
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (vehicleTypeId: number) => {
        const vt = vehicleTypes?.find(v => String(v.id) === String(vehicleTypeId));
        const name = vt ? vt.nameEn : undefined;
        return <span style={{ whiteSpace: 'nowrap' }}>{name || '-'}</span>;
      }
    },
    {
      title: t('pickup.vehicle.columns.fuelType'), 
      dataIndex: 'fuelTypeName', 
      key: 'fuelTypeName', 
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    {     
      title: t('pickup.vehicle.columns.lengthFt'), 
      dataIndex: 'lengthFt', 
      key: 'lengthFt', 
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    { 
      title: t('pickup.vehicle.columns.widthFt'), 
      dataIndex: 'widthFt', 
      key: 'widthFt', 
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    { 
      title: t('pickup.vehicle.columns.heightFt'), 
      dataIndex: 'heightFt', 
      key: 'heightFt', 
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    { 
      title: t('pickup.vehicle.columns.maxWeightLbs'),
      dataIndex: 'maxWeightLbs', 
      key: 'maxWeightLbs', 
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    { 
      title: t('pickup.vehicle.columns.maxVolumeFt3'),
      dataIndex: 'maxVolumeFt3', 
      key: 'maxVolumeFt3', 
      align: 'left' as const,
      width: 140,
      ellipsis: true,
      render: (value: number) => {
        const text = formatNumberWithCommas(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    { 
      title: t('pickup.vehicle.columns.warehouseName'), 
      dataIndex: 'warehouseName', 
      key: 'warehouseName', 
      align: 'left' as const,
      width: 160,
      ellipsis: true,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    { 
      title: t('pickup.vehicle.columns.status'), 
      dataIndex: 'status', 
      key: 'status', 
      align: 'left' as const,
      width: 120,
      ellipsis: true,
      render: (status: number) => {
        const color = status === 1 ? 'text-green-600' : 'text-gray-400';
        const text = status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended');
        return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
  ];

  return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
          {/* 筛选区 */}
          <FilterArea filters={tempFilters} onFilterChange={handleFilterChange} modelOptions={modelOptions} statusOptions={statusOptions} />
          {/* 批量操作区域 */}
          <div className="flex justify-end items-center mb-3 pl-0">
            <div className="flex items-center gap-2">
              <Button
                  type="primary"
                  onClick={showNewDrawer}
                  className="w-auto min-w-24"
              >
                  <span className="font-bold">{t('pickup.vehicle.buttons.new')}</span>
              </Button>
                <Button
                    type="primary"
                    onClick={applyFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('pickup.vehicle.buttons.filter')}</span>
                </Button>
                <Button
                    onClick={resetFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('pickup.vehicle.buttons.reset')}</span>
                </Button>
            </div>
          </div>
          {/* 表格 */}
          <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={pagedVehicles}
              pagination={false}
              scroll={{x: 'max-content'}}
              size="middle"
              tableLayout="fixed"
           className="mb-4"/>

          {/* 分页 */}
          <TablePaginationBar
            total={allVehicles.length}
            current={pagination.current}
            pageSize={pagination.pageSize}
            onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
            onShowSizeChange={(_, size) => handleTableChange({ current: 1, pageSize: size })}
            pageSizeOptions={['10', '20', '50', '100']}
          />
          {/* 新建/编辑抽屉 */}
          <VehicleFormDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onSubmit={handleDrawerSubmit}
            initialValues={selectedVehicle || undefined}
            mode={drawerMode}
            modelOptions={modelOptions}
            statusOptions={statusOptions}
          />
        </div>
  );
};

export default VehiclePage;